const DEFAULT_BACKEND = 'https://us-central1-holostaff-ai.cloudfunctions.net'

// ---------------------------------------------------------------------------
// Response types (match widgetSession Cloud Function output)
// ---------------------------------------------------------------------------

export interface StaffConfig {
  name: string
  persona: string | null
  instructions: string | null
  openingText: string | null
  voiceId: string
}

export interface WidgetSessionResponse {
  realtimeEphemeralKey: string
  realtimeWebrtcUrl: string
  anamSessionToken: string
  staff: StaffConfig
}

export interface StaffPublicConfig {
  name: string
  persona: string | null
  anamAvatarId: string | null
  avatarImageUrl: string | null
  idleVideoUrl: string | null
  openingText: string | null
  widgetPrompts: string[] | null
  voiceId: string
  // Widget display settings (from RTDB, used as defaults if embed attrs not set)
  trigger: string | null
  position: string | null
  theme: string | null
  scrollThreshold: number | null
  delayMs: number | null
}

// ---------------------------------------------------------------------------
// API calls
// ---------------------------------------------------------------------------

export async function fetchWidgetSession(
  staffId: string,
  backendUrl = DEFAULT_BACKEND,
): Promise<WidgetSessionResponse> {
  const res = await fetch(`${backendUrl}/widgetSession`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ staffId }),
  })

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }))
    throw new Error(body.error || `Session request failed: ${res.status}`)
  }

  return res.json()
}

export async function fetchWidgetConfig(
  staffId: string,
  backendUrl = DEFAULT_BACKEND,
): Promise<StaffPublicConfig> {
  const res = await fetch(
    `${backendUrl}/widgetConfig?staffId=${encodeURIComponent(staffId)}`,
  )

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }))
    throw new Error(body.error || `Config request failed: ${res.status}`)
  }

  return res.json()
}

export async function sendKeepalive(
  sessionId: string,
  sessionToken: string,
  backendUrl = DEFAULT_BACKEND,
): Promise<void> {
  const res = await fetch(`${backendUrl}/widgetKeepalive`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sessionId, sessionToken }),
  })

  if (!res.ok) {
    console.warn('[backend] Keep-alive failed:', res.status)
  }
}

export interface PageActionResponse {
  actions: Array<{
    action: string
    index: number
    value?: string
    description: string
  }>
}

export async function postPageAction(
  staffId: string,
  domSnapshot: string,
  instruction: string,
  backendUrl = DEFAULT_BACKEND,
): Promise<PageActionResponse> {
  const res = await fetch(`${backendUrl}/widgetPageAction`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ staffId, domSnapshot, instruction }),
  })

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }))
    throw new Error(body.error || `Page action request failed: ${res.status}`)
  }

  return res.json()
}
